import {Action, createReducer, on} from "@ngrx/store";
import {
	AttachedQuestion,
	AuditType,
	BinaryQuestion,
	EstimateQuestion,
	Fee,
	Order,
	PriceFactor,
	Product,
	Risk,
	Scope,
	ScopeQuestion,
	StandardName,
	TextQuestion,
	TextQuestionType,
	TransferQuestions
} from "../state/order.state";
import {
	initialAdditionalInitialCertificationQuestions,
	initialAdditionalTransferQuestions,
	initialBillingInformation,
	initialCompanyState,
	initialContactPerson,
	initialOrderState,
	initialProductState
} from "../state/order-initial.state";
import {AdditionalQuestionsData, BinaryQuestionData, DataForProduct, OrderActions} from "../actions/order.actions";
import {ReviewerActions} from "../actions/reviewer.actions";
import _ from 'lodash'
import {ImmutableOps} from "./immutableOps";

function selectStandard(state: Order, standardName: StandardName, selected: boolean): Order {
	if (selected) {
		let newProduct = ImmutableOps.for(initialProductState).set('standard.name', standardName).getObj();
		let comparator = (p1: Product, p2: Product) => compareNumbers(Object.values(StandardName).indexOf(p1.standard.name), Object.values(StandardName).indexOf(p2.standard.name));
		return ImmutableOps.for(state).arrayOps('products').push(newProduct).sort(comparator).getObj();
	} else {
		return ImmutableOps.for(state).arrayOps('products').drop((product: Product) => product.standard.name == standardName).getObj();
	}
}

function compareNumbers(n1: number, n2: number): number {
	return n1 === n2 ? 0 : (n1 > n2 ? 1 : -1);
}

function setHasIsoCertificate(state: Order, standardName: StandardName, hasCertificate: boolean): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.standard.hasCertificate`, hasCertificate).getObj();
}

function setHasDekraCertificate(state: Order, standardName: StandardName, hasDekraCertificate: boolean): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.standard.hasDekraCertificate`, hasDekraCertificate).getObj();
}

function setOtherCbName(state: Order, standardName: StandardName, otherCbName: string): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.standard.otherCbName`, otherCbName).getObj();
}

function setCbNotOnList(state: Order, standardName: StandardName, notOnList: boolean): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.standard.customCb`, notOnList).getObj();
}

function setLastAuditEvent(state: Order, standardName: StandardName, lastAudit: AuditType): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.transferQuestions`, {lastAuditEvent: lastAudit} as TransferQuestions).getObj();
}

function setEmployees(state: Order, standardName: StandardName, employees: number): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.employees`, employees).getObj();
}

function setScopeDescriptionNeedsNarrowing(state: Order, standardName: StandardName, needsNarrowing: boolean): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.scopeDescriptionNeedsNarrowing`, needsNarrowing).getObj();
}

function setScopeDescription(state: Order, standardName: StandardName, scopeDescription: string): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.scopeDescription`, scopeDescription).getObj();
}

function setEmployeesWithinScope(state: Order, standardName: StandardName, employeesWithinScope: number): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.employeesWithinScope`, employeesWithinScope).getObj();
}

function setPartTimeEmployeesIncluded(state: Order, standardName: StandardName, partTimeEmployeesIncluded: boolean): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.partTimeOrTempEmployeesIncluded`, partTimeEmployeesIncluded).getObj();
}

function setEmployeesCountDescription(state: Order, standardName: StandardName, employeesCountDescription: string): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.employeesCountDescription`, employeesCountDescription).getObj();
}

function setScopes(state: Order, standardName: StandardName, scopes: Scope[]): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.scopes`, scopes).getObj();
}

function setCompanyDescription(state: Order, standardName: StandardName, description): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.companyDescription`, description).getObj();
}

function setPriceFactorAnswer(state: Order, standardName: StandardName, priceFactorType: string, positiveAnswer: boolean, priceFactorQuestion: string): Order {
	let productIndex = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).arrayOps(`products.${productIndex}.priceFactors`)
		.updateOrPush(
			(pf: PriceFactor) => pf.type === priceFactorType,
			(pf: PriceFactor) => {
				pf.positiveAnswer = positiveAnswer;
				pf.question = priceFactorQuestion
			},
			() => ({type: priceFactorType, positiveAnswer, question: priceFactorQuestion} as PriceFactor)
		).getObj();
}

function setPriceFactorAttachedQuestionAnswer(state: Order, standardName: StandardName, priceFactorType: string, attachedQuestionType: string, answer: boolean): Order {
	const productIndex = state.products.findIndex(product => product.standard.name === standardName);
	const priceFactorIndex = state.products[productIndex].priceFactors.findIndex((pf: PriceFactor) => pf.type === priceFactorType);
	return ImmutableOps.for(state).arrayOps(`products.${productIndex}.priceFactors.${priceFactorIndex}.attachedQuestions`, true)
		.updateOrPush(
			(q: AttachedQuestion) => q.type === attachedQuestionType,
			(q: AttachedQuestion) => q.answer = answer,
			() => ({type: attachedQuestionType, answer} as AttachedQuestion)
		).getObj();
}

function setEstimateQuestionsAttachedQuestionAnswer(state: Order, standardName: StandardName, estimateQuestionId: string, attachedQuestion: AttachedQuestion, answer: any): Order {
	const productIndex = state.products.findIndex(product => product.standard.name === standardName);
	const questionIndex = state.products[productIndex].estimateQuestions.findIndex(eq => eq.questionId == estimateQuestionId)
	return ImmutableOps.for(state).arrayOps(`products.${productIndex}.estimateQuestions.${questionIndex}.attachedQuestions`)
		.updateOrPush(
			(q: AttachedQuestion) => q.type === attachedQuestion.type,
			(q: AttachedQuestion) => q.answer = answer,
			() => ({
				type: attachedQuestion.type,
				answer: answer,
				parentQuestionAnswerValue: attachedQuestion.parentQuestionAnswerValue,
				questionType: attachedQuestion.questionType,
				question: attachedQuestion.question
			} as AttachedQuestion)
		).getObj();
}

function setShiftsNumber(state: Order, standardName: StandardName, shifts: number) {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.shifts`, shifts).getObj();
}

function setCatastrophicRisk(state: Order, standardName: StandardName, catastrophicRisk: boolean) {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.catastrophicRisk`, catastrophicRisk).getObj();
}

function setOverriddenRisk(state: Order, standardName: StandardName, overriddenRisk: Risk): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.pricing.pricingAdjustments.risk`, overriddenRisk).getObj();
}

function setItComplexity(state: Order, standardName: StandardName, value: Risk) {
	value = value == Risk.NO_RISK ? null : value;
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.pricing.pricingAdjustments.itComplexity`, value).getObj();
}

function setBusinessComplexity(state: Order, standardName: StandardName, value: Risk) {
	value = value == Risk.NO_RISK ? null : value;
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.pricing.pricingAdjustments.businessComplexity`, value).getObj();
}

function setAuditEffort(state: Order, standardName: StandardName, value: Risk) {
	value = value == Risk.NO_RISK ? null : value;
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.pricing.pricingAdjustments.auditEffort`, value).getObj();
}

function setComplexityFactor(state: Order, standardName: StandardName, value: Risk) {
	value = value == Risk.NO_RISK ? null : value;
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.pricing.pricingAdjustments.complexityFactor`, value).getObj();
}

function setQuestionScopeAnswer(state: Order, standardName: StandardName, scopeCode: string, questionType: string, answer: boolean) {
	const scopes = _.cloneDeep(state.products.find(product => product.standard.name === standardName).scopes);
	let scope = scopes.find(scope => scope.code === scopeCode);
	if (scope) {
		const question = scope.questions.find(q => q.type === questionType);
		question.answer = answer;
	}

	const productIndex = state.products.findIndex(product => product.standard.name === standardName);
	const scopeIndex = state.products[productIndex].scopes.findIndex(scope => scope.code === scopeCode);
	return ImmutableOps.for(state).arrayOps(`products.${productIndex}.scopes.${scopeIndex}.questions`, true)
		.updateIfExist(
			(q: ScopeQuestion) => q.type === questionType,
			(q: ScopeQuestion) => q.answer = answer,
		).getObj();
}

function setupAdditionalQuestionsForProduct(state: Order, dataForProduct: DataForProduct) {
	const productIndex: number = state.products.findIndex(p => p.standard.name == dataForProduct.standardName);
	const textQuestions: TextQuestion[] = dataForProduct.textQuestions.map(question => {
		return {question: question.question, answer: undefined, type: question.type} as TextQuestion;
	});

	const binaryQuestions: BinaryQuestion[] = dataForProduct.binaryQuestions.map(question => {
		return {
			type: question.type,
			question: question.question,
			answer: undefined,
			commentMandatoryWithAnswerValueEqualTo: question.commentMandatoryWithAnswerValueEqualTo
		} as BinaryQuestion;
	});

	return ImmutableOps.for(state)
		.set(`products.${productIndex}.additionalQuestions.textQuestions`, textQuestions)
		.set(`products.${productIndex}.additionalQuestions.binaryQuestions`, binaryQuestions)
		.set(`products.${productIndex}.additionalQuestions.outsourcedProcesses`, [])
		.set(`products.${productIndex}.additionalQuestions.transferQuestions`, initialAdditionalTransferQuestions)
		.set(`products.${productIndex}.additionalQuestions.initialCertificationQuestions`, initialAdditionalInitialCertificationQuestions)
		.getObj();
}

function setupMissingAdditionalQuestionsForProduct(state: Order, dataForProduct: DataForProduct) {
	let immutableOps = ImmutableOps.for(state);
	const productIndex: number = state.products.findIndex(p => p.standard.name == dataForProduct.standardName);

	const additionalQuestions = state.products[productIndex].additionalQuestions;
	if (!additionalQuestions) {
		return immutableOps.getObj();
	}

	let numberOfQuestions = additionalQuestions.textQuestions.length;
	const numberOfConfigQuestions = dataForProduct.textQuestions.length;

	if (numberOfQuestions == numberOfConfigQuestions) {
		return immutableOps.getObj();
	}

	const DEFAULT_QUESTIONS = [
		TextQuestionType.MATERIALS_DESCRIPTION,
		TextQuestionType.PERMITS_IN_EFFECT,
		TextQuestionType.SAFETY_CONSIDERATIONS,
		TextQuestionType.INFORMATION_BEING_PROCESSED_OR_CONTROLLED,
		TextQuestionType.ISO_27701_CONTROLS_IMPLEMENTED
	];
	const missingQuestions: TextQuestion[] = dataForProduct.textQuestions
		.filter(question => !DEFAULT_QUESTIONS.includes(question.type))
		.map(question => {
			return {
				question: question.question,
				answer: undefined,
				type: question.type,
				group: question.group
			} as TextQuestion;
		});

	return immutableOps.arrayOps(`products.${productIndex}.additionalQuestions.textQuestions`).concat(missingQuestions).getObj();
}

function setupAdditionalQuestions(state: Order, data: AdditionalQuestionsData) {
	data.productData.forEach(pd => state = setupAdditionalQuestionsForProduct(state, pd));
	return state;
}

function setupAdditionalQuestionsMissingGroups(state: Order, data: AdditionalQuestionsData) {
	data.productData.forEach(pd => state = setupMissingAdditionalQuestionsForProduct(state, pd));
	return state;
}

function answerTextQuestion(state: Order, standardName: StandardName, answer: string, questionIndex: number) {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).arrayOps(`products.${productIndex}.additionalQuestions.textQuestions`)
		.updateItemAtIndex(questionIndex, (q: TextQuestion) => q.answer = answer)
		.getObj();
}

function answerBinaryQuestion(state: Order, standardName: StandardName, answer: boolean, binaryQuestion: BinaryQuestionData) {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).arrayOps(`products.${productIndex}.additionalQuestions.binaryQuestions`)
		.updateOrPush(
			(q: BinaryQuestion) => q.type == binaryQuestion.type,
			(q: BinaryQuestion) => q.answer = answer,
			() => ({
				answer: answer,
				type: binaryQuestion.type,
				question: binaryQuestion.question,
				commentMandatoryWithAnswerValueEqualTo: binaryQuestion.commentMandatoryWithAnswerValueEqualTo,
			} as BinaryQuestion)
		)
		.getObj();
}

function addBinaryQuestionDescription(state: Order, standardName: StandardName, description: string, questionType: string) {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).arrayOps(`products.${productIndex}.additionalQuestions.binaryQuestions`)
		.updateIfExist(
			(q: BinaryQuestion) => q.type == questionType,
			(q: BinaryQuestion) => q.description = description,
		).getObj();
}

function selectOutsourcedProcess(state: Order, standard: StandardName, outsourcedProcess: string, selected: boolean) {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standard);
	let newOutsourcedProcesses: string[];
	if (selected && !state.products[productIndex].additionalQuestions.outsourcedProcesses.includes(outsourcedProcess)) {
		newOutsourcedProcesses = state.products[productIndex].additionalQuestions.outsourcedProcesses.concat(outsourcedProcess);
	} else if (!selected) {
		newOutsourcedProcesses = state.products[productIndex].additionalQuestions.outsourcedProcesses.filter(op => op != outsourcedProcess);
	} else {
		newOutsourcedProcesses = state.products[productIndex].additionalQuestions.outsourcedProcesses;
	}

	return ImmutableOps.for(state).set(`products.${productIndex}.additionalQuestions.outsourcedProcesses`, newOutsourcedProcesses).getObj();
}

function setTransferReason(state: Order, standardName: StandardName, transferReason: string): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.additionalQuestions.transferQuestions.transferReason`, transferReason).getObj();
}

function setIsCurrentCertificationBodyAccredited(state: Order, standardName: StandardName, isCurrentCertificationBodyAccredited: boolean): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.additionalQuestions.transferQuestions.isCurrentCertificationBodyAccredited`, isCurrentCertificationBodyAccredited).getObj();
}

function setHasOpenNonConformities(state: Order, standardName: StandardName, hasOpenNonConformities: boolean): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.additionalQuestions.transferQuestions.hasOpenNonConformities`, hasOpenNonConformities).getObj();
}

function setHasCorrectiveActionPlans(state: Order, standardName: StandardName, hasCorrectiveActionPlans: boolean): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.additionalQuestions.transferQuestions.hasCorrectiveActionPlans`, hasCorrectiveActionPlans).getObj();
}

function setNumberOfMajorNonConformities(state: Order, standardName: StandardName, numberOfMajorNonConformities: number): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.additionalQuestions.transferQuestions.numberOfMajorNonConformities`, numberOfMajorNonConformities).getObj();
}

function setNumberOfMinorNonConformities(state: Order, standardName: StandardName, numberOfMinorNonConformities: number): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.additionalQuestions.transferQuestions.numberOfMinorNonConformities`, numberOfMinorNonConformities).getObj();
}

function setLastAuditPerformedDate(state: Order, standardName: StandardName, lastAuditPerformedDate: Date): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.additionalQuestions.transferQuestions.lastAuditPerformedDate`, formatDate(lastAuditPerformedDate)).getObj();
}

function setExpirationDate(state: Order, standardName: StandardName, expirationDate: Date): Order {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.additionalQuestions.transferQuestions.expirationDate`, formatDate(expirationDate)).getObj();
}

function selectCustomOutsourcedProcess(state: Order, standard: StandardName, customOutsourcedProcess: string, predefinedProcesses: string[]) {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standard);
	const newOutsourcedProcesses: string[] = state.products[productIndex].additionalQuestions.outsourcedProcesses.filter(op => predefinedProcesses.includes(op)).concat(customOutsourcedProcess);
	return ImmutableOps.for(state).set(`products.${productIndex}.additionalQuestions.outsourcedProcesses`, newOutsourcedProcesses).getObj();
}

function setPreAuditRequested(state: Order, standardName: StandardName, requested: boolean) {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.additionalQuestions.initialCertificationQuestions.preAuditRequested`, requested).getObj();
}

function setPreAuditDate(state: Order, standardName: StandardName, date: Date) {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.additionalQuestions.initialCertificationQuestions.desiredPreAuditDate`, formatDate(date)).getObj();
}

function setStage1AuditDate(state: Order, standardName: StandardName, date: Date) {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.additionalQuestions.initialCertificationQuestions.desiredStage1AuditDate`, formatDate(date)).getObj();
}

function setNextAuditDate(state: Order, standardName: StandardName, date: Date) {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standardName);
	return ImmutableOps.for(state).set(`products.${productIndex}.additionalQuestions.desiredNextAuditDate`, formatDate(date)).getObj();
}

function setAdditionalQuestions(state: Order, order: Order) {
	return ImmutableOps.for(state).arrayOps('products').performForEach((productToUpdate: Product) => {
		let product = order.products.find(p => p.standard.name == productToUpdate.standard.name);
		let productOps = ImmutableOps.for(productToUpdate)
			.set('additionalQuestions', product.additionalQuestions)
			.set('pricing.annualPricings', product.pricing.annualPricings)
			.set('pricing.dailyRate', product.pricing.dailyRate)
			.set('pricing.totalPrice', product.pricing.totalPrice)
			.set('pricing.risk', product.pricing.risk)
			.set('pricing.complexityFactor', product.pricing.complexityFactor)
			.set('pricing.totalAuditTime', product.pricing.totalAuditTime);

		if (product.pricing.pricingAdjustments != null) {
			productOps = productOps.set('pricing.pricingAdjustments.dailyRate', product.pricing.pricingAdjustments.dailyRate)
				.set('pricing.pricingAdjustments.fees', product.pricing.pricingAdjustments.fees);
		}

		return productOps;
	}).getObj();
}

function setEstimateQuestionAnswer(state: Order, standardName: StandardName, question: EstimateQuestion, answer: string[]) {
	question.chosenOptionIds = answer;
	return ImmutableOps.for(state).arrayOps('products').performOnItems((p: Product) => p.standard.name == standardName, (product: Product) => {
		return ImmutableOps.for(product)
			.setIf(() => product.estimateQuestions == null, 'estimateQuestions', [])
			.arrayOps('estimateQuestions')
			.updateOrPush(
				(q: EstimateQuestion) => q.questionId == question.questionId,
				(q: EstimateQuestion) => q.chosenOptionIds = answer,
				() => question
			);
	}).getObj();
}

function setDailyRate(state: Order, standard: StandardName, dailyRate: number) {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standard);
	return ImmutableOps.for(state).setIf(
		() => dailyRate != state.products[productIndex].pricing.dailyRate,
		`products.${productIndex}.pricing.pricingAdjustments.dailyRate`,
		dailyRate
	).getObj();
}

function setPricingAdjustmentsFees(state: Order, standard: StandardName, feeToSet: Fee) {
	return ImmutableOps.for(state).arrayOps(`products`).performOnItems((p: Product) => p.standard.name == standard, (product: Product) => {
		let ops: any = ImmutableOps.for(product)
			.setIf(() => feeToSet == null, 'pricing.pricingAdjustments.fees', [])

		if (feeToSet != null) {
			if (!feeToSet.applied) {
				ops = ops.arrayOps('pricing.pricingAdjustments.fees').drop((fee: Fee) => fee.type == feeToSet.type);
			} else {
				ops = ops.arrayOps('pricing.pricingAdjustments.fees').updateOrPush(
					(fee: Fee) => fee.type == feeToSet.type,
					(fee: Fee) => {
						fee.amount = feeToSet.amount;
						fee.applied = feeToSet.applied;
					},
					() => feeToSet
				);
			}
		}

		return ops;
	}).getObj();
}

function setContactPersonData(state: Order, order: Order) {
	return ImmutableOps.for(state)
		.set('orderUuid', order.orderUuid)
		.set('company.name', order.company.name)
		.set('company.street', order.company.street)
		.set('company.city', order.company.city)
		.set('company.zipCode', order.company.zipCode)
		.set('company.state', order.company.state)
		.set('company.isMultiSite', order.company.isMultiSite)
		.set('company.country', order.company.country)
		.set('company.additionalParams', order.company.additionalParams)
		.set('contactPerson.firstName', order.contactPerson.firstName)
		.set('contactPerson.lastName', order.contactPerson.lastName)
		.set('contactPerson.jobTitle', order.contactPerson.jobTitle)
		.set('contactPerson.phoneNumber', order.contactPerson.phoneNumber)
		.set('contactPerson.email', order.contactPerson.email)
		.set('contactPerson.salesperson', order.contactPerson.salesperson)
		.arrayOps('products')
		.updateAll((product: Product) => product.standard.hasCertificate = order.products.find(p => p.standard.name == product.standard.name).standard.hasCertificate)
		.getObj();
}

function setCompanyAdditionalParams(state: Order, paramName: string, value: string) {
	return ImmutableOps.for(state).set(`company.additionalParams.${paramName}`, value).getObj();
}

function setBasicOrderData(state: Order, order: Order) {
	return ImmutableOps.for(setContactPersonData(state, order))
		.performIf(() => order.termsAndConditions != null, (obj: Order) => {
			return ImmutableOps.for(obj)
				.set('termsAndConditions.companyName', order.termsAndConditions.companyName)
				.set('termsAndConditions.firstName', order.termsAndConditions.firstName)
				.set('termsAndConditions.lastName', order.termsAndConditions.lastName)
				.set('termsAndConditions.jobTitle', order.termsAndConditions.jobTitle)
		})
		.arrayOps('products').performForEach((product: Product) => {
			let srcProduct = order.products.find(p => p.standard.name == product.standard.name);
			return ImmutableOps.for(product)
				.set('scopes', srcProduct.scopes)
				.set('scopeDescriptionNeedsNarrowing', srcProduct.scopeDescriptionNeedsNarrowing)
				.set('scopeDescription', srcProduct.scopeDescription)
				.set('employees', srcProduct.employees)
				.set('employeesWithinScope', srcProduct.employeesWithinScope)
				.set('partTimeOrTempEmployeesIncluded', srcProduct.partTimeOrTempEmployeesIncluded)
				.set('employeesCountDescription', srcProduct.employeesCountDescription)
				.set('companyDescription', srcProduct.companyDescription)
				.set('priceFactors', srcProduct.priceFactors)
				.set('standard.otherCbName', srcProduct.standard.otherCbName)
				.set('catastrophicRisk', srcProduct.catastrophicRisk)
				.set('shifts', srcProduct.shifts)
				.set('pricing.annualPricings', srcProduct.pricing.annualPricings)
				.set('pricing.dailyRate', srcProduct.pricing.dailyRate)
				.set('pricing.totalPrice', srcProduct.pricing.totalPrice)
				.set('pricing.risk', srcProduct.pricing.risk)
				.set('pricing.complexityFactor', srcProduct.pricing.complexityFactor)
				.set('pricing.auditEffort', srcProduct.pricing.auditEffort)
				.set('pricing.businessComplexity', srcProduct.pricing.businessComplexity)
				.set('pricing.itComplexity', srcProduct.pricing.itComplexity)
				.set('pricing.totalAuditTime', srcProduct.pricing.totalAuditTime)
				.setIf(() => srcProduct.transferQuestions != null, 'transferQuestions.lastAuditEvent', srcProduct.transferQuestions.lastAuditEvent)
				.setIf(() => srcProduct.pricing.pricingAdjustments != null, 'pricing.pricingAdjustments.fees', srcProduct.pricing.pricingAdjustments.fees)
				.setIf(() => srcProduct.pricing.pricingAdjustments != null, 'pricing.pricingAdjustments.dailyRate', srcProduct.pricing.pricingAdjustments.dailyRate)
				.setIf(() => srcProduct.standard.otherCbName != null, 'standard.hasDekraCertificate', false)
				.setIf(() => srcProduct.estimateQuestions != null, 'estimateQuestions', srcProduct.estimateQuestions)
		}).getObj();
}

function addNewTextQuestions(state: Order, standard: StandardName, newQuestions: TextQuestion[]) {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standard);
	return ImmutableOps.for(state).arrayOps(`products.${productIndex}.additionalQuestions.textQuestions`).concat(newQuestions).getObj();
}

function addNewBinaryQuestions(state: Order, standard: StandardName, newQuestions: BinaryQuestion[]) {
	const productIndex: number = state.products.findIndex(p => p.standard.name == standard);
	return ImmutableOps.for(state).arrayOps(`products.${productIndex}.additionalQuestions.binaryQuestions`).concat(newQuestions).getObj();
}

function setInitialBillingContactData(state: Order, sameCompanyAndContactPersonAsProvided: boolean) {
	const previousState = state.billingInformations.sameCompanyAndContactPersonAsProvided;
	return ImmutableOps.for(state)
		.set('billingInformations.sameCompanyAndContactPersonAsProvided', sameCompanyAndContactPersonAsProvided)
		.performIf(
			() => previousState && !sameCompanyAndContactPersonAsProvided || !previousState && sameCompanyAndContactPersonAsProvided,
			(order: Order) => ImmutableOps.for(order)
				.set('billingInformations.billingCompany', initialCompanyState)
				.set('billingInformations.billingContactPerson', initialContactPerson)
		).getObj();
}

function replaceEmptyString(value: string) {
	return value === '' ? null : value;
}

function initBillingPreferences(state: Order): Order {
	return ImmutableOps.for(state).setIf(() => state.billingInformations == null, 'billingInformations', ({...initialBillingInformation, initial: true})).getObj();
}

const reducer = createReducer(
	initialOrderState,
	on(OrderActions.SET_ORDER, (state, action) => {
		const newState = ImmutableOps.for(action.order).getObj();
		return initBillingPreferences(newState);
	}),
	on(OrderActions.SET_ORDER_UUID, (state, action) => ImmutableOps.for(state).set('orderUuid', action.uuid).getObj()),
	on(OrderActions.SET_BASIC_ORDER_DATA, (state, action) => setBasicOrderData(state, action.order)),
	on(OrderActions.SET_CONTACT_PERSON_DATA, (state, action) => setContactPersonData(state, action.order)),
	on(OrderActions.SET_ADDITIONAL_QUESTIONS, (state, action) => setAdditionalQuestions(state, action.order)),
	on(OrderActions.CLEAR_ORDER, (state, action) => ImmutableOps.for(initialOrderState).getObj()),
	on(OrderActions.ORDER_REQUEST_SUCCESSFUL, (state, action) => action.orderRequestResponse.body.order),
	on(OrderActions.REQUEST_ESTIMATE_SUCCESSFUL, (state, action) => ImmutableOps.for(state).set('status', action.response.body.status).getObj()),
	on(OrderActions.SEND_ADDITIONAL_QUESTIONS_SUCCESSFUL, (state, action) => ImmutableOps.for(state).set('status', action.sendResponse.body.status).getObj()),
	on(OrderActions.SELECT_STANDARD, (state, action) => selectStandard(state, action.standard, action.selected)),
	on(OrderActions.SET_COMPANY_NAME, (state, action) => ImmutableOps.for(state).set('company.name', action.companyName).getObj()),
	on(OrderActions.SET_CITY, (state, action) => ImmutableOps.for(state).set('company.city', action.city).getObj()),
	on(OrderActions.SET_STREET, (state, action) => ImmutableOps.for(state).set('company.street', action.street).getObj()),
	on(OrderActions.SET_STATE, (state, action) => ImmutableOps.for(state).set('company.state', action.state).getObj()),
	on(OrderActions.SET_COMPANY_ADDITIONAL_PARAM, (state, action) => setCompanyAdditionalParams(state, action.paramName, action.value)),
	on(OrderActions.SET_COUNTRY, (state, action) => ImmutableOps.for(state).set('company.country', action.countryName).set('clientCountry', action.code).getObj()),
	on(OrderActions.SET_ZIP_CODE, (state, action) => ImmutableOps.for(state).set('company.zipCode', action.zipCode).getObj()),
	on(OrderActions.SET_MULTISITE, (state, action) => ImmutableOps.for(state).set('company.isMultiSite', action.multiSite).getObj()),
	on(OrderActions.SET_HAS_ISO_CERTIFICATE, (state, action) => setHasIsoCertificate(state, action.standardName, action.hasCertificate)),
	on(OrderActions.SET_HAS_DEKRA_CERTIFICATE, (state, action) => setHasDekraCertificate(state, action.standardName, action.hasDekraCertificate)),
	on(OrderActions.SET_CURRENT_CB_NAME, (state, action) => setOtherCbName(state, action.standardName, action.otherCbName)),
	on(OrderActions.SET_CB_NOT_ON_LIST, (state, action) => setCbNotOnList(state, action.standardName, action.notOnList)),
	on(OrderActions.SET_LAST_AUDIT_EVENT, (state, action) => setLastAuditEvent(state, action.standardName, action.lastAuditEvent)),
	on(OrderActions.SET_EMPLOYEES_COUNT, (state, action) => setEmployees(state, action.standardName, action.employees)),
	on(OrderActions.SET_SCOPE_DESCRIPTION_NEEDS_NARROWING, (state, action) => setScopeDescriptionNeedsNarrowing(state, action.standardName, action.needsNarrowing)),
	on(OrderActions.SET_SCOPE_DESCRIPTION, (state, action) => setScopeDescription(state, action.standardName, action.scopeDescription)),
	on(OrderActions.SET_EMPLOYEES_WITHIN_SCOPE, (state, action) => setEmployeesWithinScope(state, action.standardName, action.employees)),
	on(OrderActions.SET_PART_TIME_EMPLOYEES_INCLUDED, (state, action) => setPartTimeEmployeesIncluded(state, action.standardName, action.included)),
	on(OrderActions.SET_EMPLOYEES_COUNT_DESCRIPTION, (state, action) => setEmployeesCountDescription(state, action.standardName, action.description)),
	on(OrderActions.SET_CLIENT_SCOPES, (state, action) => setScopes(state, action.standardName, action.scopes)),
	on(OrderActions.SET_COMPANY_DESCRIPTION, (state, action) => setCompanyDescription(state, action.standardName, action.description)),
	on(OrderActions.SET_CONTACT_PERSON_FIRST_NAME, (state, action) => ImmutableOps.for(state).set('contactPerson.firstName', action.firstName).getObj()),
	on(OrderActions.SET_CONTACT_PERSON_LAST_NAME, (state, action) => ImmutableOps.for(state).set('contactPerson.lastName', action.lastName).getObj()),
	on(OrderActions.SET_CONTACT_PERSON_JOB_TITLE, (state, action) => ImmutableOps.for(state).set('contactPerson.jobTitle', action.jobTitle).getObj()),
	on(OrderActions.SET_COUNTRY_CODE, (state, action) => ImmutableOps.for(state).set('contactPerson.countryCode', action.countryCode).getObj()),
	on(OrderActions.SET_PHONE_NUMBER, (state, action) => ImmutableOps.for(state).set('contactPerson.phoneNumber', action.phoneNumber).getObj()),
	on(OrderActions.SET_CLIENT_EMAIL, (state, action) => ImmutableOps.for(state).set('contactPerson.email', action.email).getObj()),
	on(OrderActions.SET_SALESPERSON, (state, action) => ImmutableOps.for(state).set('contactPerson.salesperson', action.salesperson).getObj()),
	on(OrderActions.PRICE_FACTOR_QUESTION_ANSWERED, (state, action) => setPriceFactorAnswer(state, action.standardName, action.priceFactorType, action.positiveAnswer, action.priceFactorQuestion)),
	on(OrderActions.ESTIMATE_QUESTION_ANSWERED, (state, action) => setEstimateQuestionAnswer(state, action.standardName, action.question, action.answer)),
	on(OrderActions.PRICE_FACTOR_ATTACHED_QUESTION_ANSWERED, (state, action) => setPriceFactorAttachedQuestionAnswer(state, action.standardName, action.priceFactorType, action.attachedQuestionType, action.answer)),
	on(OrderActions.SET_SHIFTS_NUMBER, (state, action) => setShiftsNumber(state, action.standardName, action.shifts)),
	on(OrderActions.SET_CATASTROPHIC_RISK, (state, action) => setCatastrophicRisk(state, action.standardName, action.catastrophicRisk)),
	on(OrderActions.SET_SCOPE_ANSWER, (state, action) => setQuestionScopeAnswer(state, action.standardName, action.scopeCode, action.questionType, action.answer)),
	on(OrderActions.SETUP_ADDITIONAL_QUESTIONS, (state, action) => setupAdditionalQuestions(state, action.data)),
	on(OrderActions.SETUP_ADDITIONAL_QUESTIONS_MISSING_GROUPS_SUCCESSFUL, (state, action) => setupAdditionalQuestionsMissingGroups(state, action.data)),
	on(OrderActions.ANSWER_TEXT_QUESTION, (state, action) => answerTextQuestion(state, action.standardName, action.answer, action.questionIndex)),
	on(OrderActions.ANSWER_BINARY_QUESTION, (state, action) => answerBinaryQuestion(state, action.standardName, action.answer, action.binaryQuestion)),
	on(OrderActions.ADD_BINARY_QUESTION_DESCRIPTION, (state, action) => addBinaryQuestionDescription(state, action.standardName, action.description, action.questionType)),
	on(OrderActions.SELECT_OUTSOURCED_PROCESS, (state, action) => selectOutsourcedProcess(state, action.standard, action.outsourcedProcess, action.selected)),
	on(OrderActions.ADD_NEW_TEXT_QUESTIONS, (state, action) => addNewTextQuestions(state, action.standard, action.newQuestions)),
	on(OrderActions.ADD_NEW_BINARY_QUESTIONS, (state, action) => addNewBinaryQuestions(state, action.standard, action.newQuestions)),

	on(OrderActions.SET_TRANSFER_REASON, (state, action) => setTransferReason(state, action.standardName, action.transferReason)),
	on(OrderActions.SET_IS_CURRENT_CERTIFICATION_BODY_ACCREDITED, (state, action) => setIsCurrentCertificationBodyAccredited(state, action.standardName, action.isCurrentCertificationBodyAccredited)),
	on(OrderActions.SET_HAS_OPEN_NON_CONFORMITIES, (state, action) => setHasOpenNonConformities(state, action.standardName, action.hasOpenNonConformities)),
	on(OrderActions.SET_HAS_CORRECTIVE_ACTION_PLANS, (state, action) => setHasCorrectiveActionPlans(state, action.standardName, action.hasCorrectiveActionPlans)),
	on(OrderActions.SET_NUMBER_OF_MAJOR_NON_CONFORMITIES, (state, action) => setNumberOfMajorNonConformities(state, action.standardName, action.numberOfMajorNonConformities)),
	on(OrderActions.SET_NUMBER_OF_MINOR_NON_CONFORMITIES, (state, action) => setNumberOfMinorNonConformities(state, action.standardName, action.numberOfMinorNonConformities)),
	on(OrderActions.SET_LAST_AUDIT_PERFORMED_DATE, (state, action) => setLastAuditPerformedDate(state, action.standardName, action.lastAuditPerformedDate)),
	on(OrderActions.SET_EXPIRATION_DATE, (state, action) => setExpirationDate(state, action.standardName, action.expirationDate)),
	on(OrderActions.SELECT_CUSTOM_OUTSOURCED_PROCESS, (state, action) => selectCustomOutsourcedProcess(state, action.standard, action.outsourcedProcess, action.predefinedProcesses)),
	on(OrderActions.SET_PRE_AUDIT_REQUESTED, (state, action) => setPreAuditRequested(state, action.standardName, action.value)),
	on(OrderActions.SET_PRE_AUDIT_DATE, (state, action) => setPreAuditDate(state, action.standardName, action.date)),
	on(OrderActions.SET_STAGE_1_AUDIT_DATE, (state, action) => setStage1AuditDate(state, action.standardName, action.date)),
	on(OrderActions.SET_NEXT_AUDIT_DATE, (state, action) => setNextAuditDate(state, action.standardName, action.date)),

	on(OrderActions.SET_TERMS_AND_CONDITIONS_ESTIMATE_ACCEPTED, (state, action) => ImmutableOps.for(state).set('termsAndConditions.estimateAccepted', action.estimateAccepted).getObj()),
	on(OrderActions.SET_TERMS_AND_CONDITIONS_ACCEPTED, (state, action) => ImmutableOps.for(state).set('termsAndConditions.termsAndConditionsAccepted', action.termsAndConditionsAccepted).getObj()),
	on(OrderActions.SET_TERMS_AND_CONDITIONS_FIRST_NAME, (state, action) => ImmutableOps.for(state).set('termsAndConditions.firstName', action.firstName).getObj()),
	on(OrderActions.SET_TERMS_AND_CONDITIONS_LAST_NAME, (state, action) => ImmutableOps.for(state).set('termsAndConditions.lastName', action.lastName).getObj()),
	on(OrderActions.SET_TERMS_AND_CONDITIONS_JOB_TITLE, (state, action) => ImmutableOps.for(state).set('termsAndConditions.jobTitle', action.jobTitle).getObj()),
	on(OrderActions.SET_TERMS_AND_CONDITIONS_EMAIL, (state, action) => ImmutableOps.for(state).set('termsAndConditions.email', action.email).getObj()),
	on(OrderActions.SET_TERMS_AND_CONDITIONS_PHONE, (state, action) => ImmutableOps.for(state).set('termsAndConditions.phone', action.phone).getObj()),
	on(OrderActions.SET_TERMS_AND_CONDITIONS_COUNTRY_CODE, (state, action) => ImmutableOps.for(state).set('termsAndConditions.countryCode', action.countryCode).getObj()),

	on(OrderActions.DECLINE_ORDER_BY_CLIENT_SUCCESSFUL, (state, action) => ImmutableOps.for(state).set('status', action.statusChange.status).getObj()),
	on(OrderActions.GET_NEW_ESTIMATE_AFTER_DECLINE, (state, action) => ImmutableOps.for(initialOrderState).set('contactPerson.email', action.userEmail).getObj()),

	on(OrderActions.SET_DAILY_RATE, (state, action) => setDailyRate(state, action.standard, action.dailyRate)),
	on(OrderActions.SET_FEE, (state, action) => setPricingAdjustmentsFees(state, action.standard, action.fee)),

	on(OrderActions.TOGGLE_SAME_COMPANY, (state, action) => setInitialBillingContactData(state, action.sameCompanyAndContactPersonAsProvided)),
	on(OrderActions.TOGGLE_PURCHASE_PAYMENT, (state, action) => ImmutableOps.for(state).set('billingInformations.isPurchaseNumberRequiredForPayment', action.isPurchaseNumberRequiredForPayment).getObj()),
	on(OrderActions.SET_PURCHASE_NUMBER, (state, action) => ImmutableOps.for(state).set('billingInformations.purchaseOrderNumber', action.purchaseOrderNumber).getObj()),
	on(OrderActions.TOGGLE_TRAVEL_EXPENSE, (state, action) => ImmutableOps.for(state).set('billingInformations.coverTravelExpenses', action.coverTravelExpenses).getObj()),
	on(OrderActions.SET_ADDITIONAL_BILLING_INFORMATION, (state, action) => ImmutableOps.for(state).set('billingInformations.additionalInformation', action.additionalInformation).getObj()),

	on(OrderActions.SET_BILLING_CONTACT_PERSON_FIRST_NAME, (state, action) => ImmutableOps.for(state).set('billingInformations.billingContactPerson.firstName', action.firstName).getObj()),
	on(OrderActions.SET_BILLING_CONTACT_PERSON_LAST_NAME, (state, action) => ImmutableOps.for(state).set('billingInformations.billingContactPerson.lastName', action.lastName).getObj()),
	on(OrderActions.SET_BILLING_CONTACT_PERSON_JOB_TITLE, (state, action) => ImmutableOps.for(state).set('billingInformations.billingContactPerson.jobTitle', action.jobTitle).getObj()),
	on(OrderActions.SET_BILLING_COUNTRY_CODE, (state, action) => ImmutableOps.for(state).set('billingInformations.billingContactPerson.countryCode', action.countryCode).getObj()),
	on(OrderActions.SET_BILLING_PHONE_NUMBER, (state, action) => ImmutableOps.for(state).set('billingInformations.billingContactPerson.phoneNumber', action.phoneNumber).getObj()),
	on(OrderActions.SET_BILLING_CLIENT_EMAIL, (state, action) => ImmutableOps.for(state).set('billingInformations.billingContactPerson.email', action.email).getObj()),

	on(OrderActions.SET_BILLING_COMPANY_NAME, (state, action) => ImmutableOps.for(state).set('billingInformations.billingCompany.name', action.companyName).getObj()),
	on(OrderActions.SET_BILLING_CITY, (state, action) => ImmutableOps.for(state).set('billingInformations.billingCompany.city', action.city).getObj()),
	on(OrderActions.SET_BILLING_STREET, (state, action) => ImmutableOps.for(state).set('billingInformations.billingCompany.street', action.street).getObj()),
	on(OrderActions.SET_BILLING_STATE, (state, action) => ImmutableOps.for(state).set('billingInformations.billingCompany.state', action.state).getObj()),
	on(OrderActions.SET_BILLING_COUNTRY, (state, action) => ImmutableOps.for(state).set('billingInformations.billingCompany.country', action.countryName).getObj()),
	on(OrderActions.SET_BILLING_ZIP_CODE, (state, action) => ImmutableOps.for(state).set('billingInformations.billingCompany.zipCode', action.zipCode).getObj()),

	on(ReviewerActions.SET_REVIEWER_ORDER, (state, action) => {
		return ImmutableOps.for(action.order)
			.setIf(() => action.order.billingInformations == null, 'billingInformations', ({...initialBillingInformation, initial: true}))
			.getObj();
	}),

	on(ReviewerActions.UPDATE_DATA_COMPARED, (state, action) => ImmutableOps.for(state).set('dataCompared', action.dataCompared).getObj()),

	on(ReviewerActions.CANCEL_CHANGE_PROPOSAL_SUCCESSFUL, (state, action) => {
		const newState = ImmutableOps.for(action.order).getObj();
		return initBillingPreferences(newState);
	}),

	on(ReviewerActions.SET_OVERRIDDEN_RISK, (state: Order, action) => {
		return setOverriddenRisk(state, action.standardName, action.value) as unknown as Order
	}),
	on(ReviewerActions.SET_IT_COMPLEXITY, (state, action) => setItComplexity(state, action.standardName, action.value)),
	on(ReviewerActions.SET_BUSINESS_COMPLEXITY, (state, action) => setBusinessComplexity(state, action.standardName, action.value)),
	on(ReviewerActions.SET_AUDIT_EFFORT, (state, action) => setAuditEffort(state, action.standardName, action.value)),
	on(ReviewerActions.SET_COMPLEXITY_FACTOR, (state, action) => setComplexityFactor(state, action.standardName, action.value)),

	on(ReviewerActions.UPLOAD_CERTIFICATE_DOCUMENTS_SUCCESSFUL, (state, action) => ImmutableOps.for(state).set('certificateFiles', action.files).getObj()),
	on(ReviewerActions.UPLOAD_REPORT_DOCUMENTS_SUCCESSFUL, (state, action) => ImmutableOps.for(state).set('reportFiles', action.files).getObj()),
	on(ReviewerActions.REMOVE_TRANSFER_DOCUMENT_SUCCESSFUL, (state, action) => ImmutableOps.for(state).set('certificateFiles', action.order.certificateFiles).set('reportFiles', action.order.reportFiles).getObj()),

	on(OrderActions.ACCEPT_REVIEW_CHANGES_SUCCESSFUL, (state, action) => ImmutableOps.for(state).set('status', action.statusChange.status).getObj()),
	on(OrderActions.DECLINE_REVIEW_CHANGES_SUCCESSFUL, (state, action) => ImmutableOps.for(state).set('status', action.statusChange.status).getObj()),

	on(OrderActions.SET_REVIEWER_CHANGE_COMMENT, (state, action) => ImmutableOps.for(state).set('reviewerChangeComment', action.comment).getObj()),

	on(ReviewerActions.SAVE_LEAD_ADDITIONAL_QUESTIONS_SUCCESSFUL, (state: Order, action) => ImmutableOps.objectFrom(action.order)),
	on(ReviewerActions.SEND_LEAD_UPDATE_TO_CLIENT_SUCCESSFUL, (state: Order, action) => ImmutableOps.objectFrom(action.order)),
	on(ReviewerActions.CANCEL_BASIC_INFO_EDIT_SUCCESSFUL, (state: Order, action) => ImmutableOps.objectFrom(action.order)),

	on(ReviewerActions.CANCEL_TAKE_OVER_SALES_LEAD_SUCCESSFUL, (state: Order, action) => ImmutableOps.objectFrom(action.order)),

	on(OrderActions.ESTIMATE_QUESTION_ATTACHED_QUESTION_ANSWERED, (state, action) => setEstimateQuestionsAttachedQuestionAnswer(state, action.standardName, action.estimateQuestionId, action.attachedQuestion, action.answer))
);

export function formatDate(date: Date): string {
	if (date != null) {
		date = new Date(date);
		const month = date.getMonth() + 1;
		const day = date.getDate();
		return `${date.getFullYear()}-${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}`;
	} else {
		return null;
	}
}

export function orderReducer(state: Order | undefined, action: Action) {
	return reducer(state, action);
}
